import React from 'react';
import { useLabels } from 'ws-scripts/modules/common';
import { useDrawer } from '../context/DrawerContext';
import useMyCarsEnabled from '../hooks/useMyCarsEnabled';
import useFragmentLoaded from '../hooks/useFragmentLoaded';
import {
	MYCARS_HEADER_FRAGMENT_ALIAS,
	openMyCarsModal
} from '../utils/myCarsIntegration';
import DrawerNavFragment from './DrawerNavFragment';
import FragmentPlaceholder from './FragmentPlaceholder';

/**
 * Kinetic icon class shared by the placeholder and the real button, so the
 * two are visually identical apart from interactivity.
 */
const MYCARS_ICON_CLASS = 'ddc-icon ddc-icon-mycars-login ddc-icon-size-xlarge';

/**
 * Wrapper classes shared by the placeholder and the real button. Both use
 * `ws-nav-drawer__mycars` -- the same class the left-drawer order rule and
 * the box-model rules in drawer.scss key off -- so swapping between the two
 * can never shift the header row's layout (the CLS this component exists to
 * prevent).
 */
const MYCARS_WRAPPER_CLASS =
	'ws-nav-drawer__mycars drawer-header-icon d-none d-md-flex';

/**
 * MyCarsHeaderIcon — real header-row button that opens the My Cars modal.
 *
 * Visibility is gated on `useMyCarsEnabled`, the same
 * `window.DDC.siteProperties` check ws-mycars-nav itself uses and the
 * drawer's own CTA button (`DrawerCTAButtons`) also uses, so the two entry
 * points can never disagree about whether My Cars is on for this site.
 *
 * `useMyCarsEnabled` returns `null` until its client-only check resolves.
 * While unresolved (true at SSR time and on first client render, before
 * hydration), this renders the same decorative placeholder the header row
 * rendered before this feature existed, reserving its space so nothing
 * shifts once the real check resolves.
 *
 * Once resolved enabled, this mounts the headless DRAWER_MYCARS_HEADER_FRAGMENT
 * fragment so ws-mycars-nav's modal starts fetching immediately -- but keeps
 * showing the placeholder (instead of the real button) until that fragment's
 * Redux status reaches LOADED. Clicking the button before ws-mycars-nav's
 * modal listener has attached (its bundle still has to download/execute after
 * the fragment's HTML arrives) silently drops the click; gating on LOADED
 * shrinks that window without eliminating it (see US2059853 T53). Once
 * resolved disabled, this renders nothing at all.
 *
 * @returns {React.ReactElement|null}
 */
const MyCarsHeaderIcon = () => {
	const isEnabled = useMyCarsEnabled();
	const labels = useLabels();
	const { fragmentUrl, isLoaded: isFragmentLoaded } = useFragmentLoaded(
		MYCARS_HEADER_FRAGMENT_ALIAS
	);
	const { isOpen, closeDrawer } = useDrawer();

	const handleClick = () => openMyCarsModal(closeDrawer, isOpen);

	if (isEnabled === null) {
		return (
			<FragmentPlaceholder
				icon={MYCARS_ICON_CLASS}
				className={MYCARS_WRAPPER_CLASS}
				data-testid="mycars-header-placeholder"
			/>
		);
	}

	if (!isEnabled) return null;

	// If this site's My Cars setup is broken instead of just slow, the
	// fragment never finishes loading, so this keeps showing the placeholder
	// forever instead of switching to the real button below. That's fine --
	// there's no way to tell "still loading" apart from "never going to
	// load," and staying on the inert placeholder is the safer outcome.
	return (
		<>
			{isFragmentLoaded ? (
				<button
					type="button"
					className={`btn btn-link text-decoration-none ${MYCARS_WRAPPER_CLASS}`}
					aria-label={labels.get('MY_CARS')}
					data-testid="mycars-header-icon"
					onClick={handleClick}
				>
					<i
						className={MYCARS_ICON_CLASS}
						aria-hidden="true"
						data-testid="mycars-header-icon-icon"
					/>
				</button>
			) : (
				<FragmentPlaceholder
					icon={MYCARS_ICON_CLASS}
					className={MYCARS_WRAPPER_CLASS}
					data-testid="mycars-header-placeholder"
				/>
			)}
			<DrawerNavFragment url={fragmentUrl} headless />
		</>
	);
};

export default MyCarsHeaderIcon;
